/**
 * Digits, in and out.
 *
 * Two different strings describe every number this portal handles: the one a
 * person reads and the one the server is sent. They are not the same string
 * and must never be confused — «۱۲٬۵۰۰٬۰۰۰» is for a human, `12500000` is for
 * an API, and the whole point of this module is that exactly one place knows
 * how to get from either to the other.
 *
 * Everything here works on strings. Nothing returns a `number`, because a
 * partly-typed amount is not a number yet and coercing it to one loses the
 * difference between '' and 0 — the difference between 'nobody has decided'
 * and 'decided: nothing', which on this platform moves money.
 */

/** ۰ U+06F0 — Persian, what an Iranian keyboard produces. */
const PERSIAN_ZERO = 0x06f0
/** ٠ U+0660 — Arabic-Indic, what an Arabic keyboard and some pasted text produce. */
const ARABIC_INDIC_ZERO = 0x0660

/** ٬ U+066C, the grouping mark 'fa-IR' itself uses. */
export const GROUP_SEPARATOR = '٬'
/** ٫ U+066B, likewise the decimal mark. */
export const DECIMAL_SEPARATOR = '٫'

/**
 * Marks that decorate a number without being part of it.
 *
 * Latin comma and apostrophe are here because a number pasted from a
 * spreadsheet or an English invoice arrives grouped that way, and refusing it
 * would mean an operator retyping a figure they already have — which is how
 * digits get transposed.
 */
const DECORATION = /[٬,'’\s‌‏‎_]/g

/** Marks people actually type for a decimal point, including the Persian keyboard's slash. */
const DECIMAL_MARKS = /[٫،.،/]/g

/**
 * Any digit, written any of the three ways, becomes an ASCII digit.
 *
 * Only digits are touched. Letters, signs and separators are left exactly as
 * they were, so this is safe to run over a string before deciding what the
 * rest of it means.
 */
export function toCanonicalDigits(text: string): string {
  let out = ''
  for (const character of text) {
    const code = character.codePointAt(0) ?? 0
    if (code >= PERSIAN_ZERO && code <= PERSIAN_ZERO + 9) {
      out += String(code - PERSIAN_ZERO)
    } else if (code >= ARABIC_INDIC_ZERO && code <= ARABIC_INDIC_ZERO + 9) {
      out += String(code - ARABIC_INDIC_ZERO)
    } else {
      out += character
    }
  }
  return out
}

/** The other direction, for display only. */
export function toPersianDigits(text: string): string {
  return text.replace(/[0-9]/g, (digit) =>
    String.fromCodePoint(PERSIAN_ZERO + Number(digit)),
  )
}

export interface NumericShape {
  /** A leading minus is meaningful — a signed wallet adjustment, say. */
  allowNegative?: boolean
  /** Fractional part permitted, to this many places. Money never sets this. */
  decimals?: number
}

/**
 * Anything a person can type or paste, reduced to the canonical form.
 *
 * Deliberately tolerant: this runs on every keystroke, so it must accept the
 * half-finished states typing passes through — '' and '-' and '12.' are all
 * legitimate things to be holding mid-edit, and rejecting them would fight the
 * person doing the typing. Deciding whether the finished value is acceptable
 * is validation's job, and happens elsewhere on this same canonical string.
 */
export function toCanonicalNumber(
  raw: string,
  shape: NumericShape = {},
): string {
  const { allowNegative = false, decimals = 0 } = shape
  let text = toCanonicalDigits(raw).replace(DECORATION, '')

  const negative = allowNegative && text.trimStart().startsWith('-')
  text = text.replace(/-/g, '')

  if (decimals > 0) {
    text = text.replace(DECIMAL_MARKS, '.')
    const [whole = '', ...rest] = text.split('.')
    // Everything after the first point is one fractional part; a second point
    // is a typo, not a second number.
    const fraction = rest.join('')
    const digitsOnly = (value: string) => value.replace(/[^\d]/g, '')
    text = digitsOnly(whole)
    if (rest.length > 0) text += `.${digitsOnly(fraction).slice(0, decimals)}`
  } else {
    text = text.replace(/[^\d]/g, '')
  }

  return negative ? `-${text}` : text
}

/** Group the integer part in threes. Operates on canonical ASCII. */
export function groupThousands(canonicalInteger: string): string {
  const negative = canonicalInteger.startsWith('-')
  const digits = negative ? canonicalInteger.slice(1) : canonicalInteger
  const grouped = digits.replace(/\B(?=(\d{3})+(?!\d))/g, GROUP_SEPARATOR)
  return negative ? `−${grouped}` : grouped
}

export interface DisplayShape extends NumericShape {
  /** Thousands separators while editing. Money wants them; a priority does not. */
  grouping?: boolean
}

/**
 * The canonical string as a person should read it.
 *
 * A trailing decimal point survives, because the person is mid-way through
 * typing one and removing it would delete the keystroke they just made.
 */
export function toDisplayNumber(
  canonical: string,
  shape: DisplayShape = {},
): string {
  const { grouping = false } = shape
  if (canonical === '' || canonical === '-') {
    return canonical === '-' ? '−' : ''
  }
  const [whole = '', fraction] = canonical.split('.')
  const head = grouping ? groupThousands(whole) : whole.replace('-', '−')
  const tail = fraction === undefined ? '' : `${DECIMAL_SEPARATOR}${fraction}`
  return toPersianDigits(`${head}${tail}`)
}

/**
 * How many digits precede this offset.
 *
 * The anchor a caret can be restored from: separators come and go as the
 * number is formatted, digits do not.
 */
export function countDigitsBefore(text: string, offset: number): number {
  return toCanonicalDigits(text.slice(0, offset)).replace(/[^\d]/g, '').length
}

/** Where in `display` the caret sits once `count` digits have gone by. */
export function offsetAfterDigits(display: string, count: number): number {
  if (count === 0) {
    // Before the first digit, not before a leading minus sign.
    const first = display.search(/[0-9۰-۹]/)
    return first === -1 ? display.length : first
  }
  let seen = 0
  for (let index = 0; index < display.length; index += 1) {
    if (/[0-9۰-۹]/.test(display[index] ?? '')) {
      seen += 1
      if (seen === count) return index + 1
    }
  }
  return display.length
}

/**
 * The canonical value as an integer, or null.
 *
 * Null covers both 'not a number' and '' — and '' is not zero. Callers that
 * treat those the same are the bug this platform keeps having.
 */
export function toInteger(canonical: string): number | null {
  if (!/^-?\d+$/.test(canonical)) return null
  const parsed = Number(canonical)
  return Number.isSafeInteger(parsed) ? parsed : null
}
